import FormBuilderSettingsCard from 'pages/Components/FormBuilderSettingsCard'
import * as React from 'react'
import { createPortal } from 'react-dom'
import ToggleField from 'ui/molecules/ToggleField'

import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { SectionFormSettings } from '@/ui/atoms/SectionFormSettings'
import { ZentakeContext } from '@/utils/ZentakeContext'

import { ChangeMeasuresTracking } from './Components/ChangeMeasuresTracking'
import { ConfirmLockingFormModal } from './Components/ConfirmLockingFormModal/ConfirmLockingFormModal'
import FormBuilderSettingsGeneralDocumentConsumer from './Components/FormBuilderSettingsGeneralDocumentConsumer'
import FormBuilderSettingsGeneralFormConsumer from './Components/FormBuilderSettingsGeneralFormConsumer'

type Props = {
  resourceType?: 'document' | 'form'
}

function FormBuilderSettingsGeneral({ resourceType = 'form' }: Props) {
  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()
  const { teamDetails } = React.useContext(ZentakeContext)

  const isLockingForm = searchParams.get('locking_form') === 'true'
  const measuresEnabled = teamDetails?.measures

  const Consumer =
    resourceType === 'document'
      ? FormBuilderSettingsGeneralDocumentConsumer
      : FormBuilderSettingsGeneralFormConsumer

  return (
    <Consumer>
      {({
        formDownloadable,
        internalForm,
        measuresEnabledForm,
        setFormDownloadable,
        setInternalForm,
        setMeasuresEnabledForm,
        setIsDirty,
      }) => {
        return (
          <>
            <FormBuilderSettingsCard title="General Settings">
              <div className="flex flex-col gap-6">
                <SectionFormSettings.Root>
                  <ToggleField
                    compact
                    horizontal
                    primary
                    label="Downloadable by client after submission"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFormDownloadable(e.currentTarget.checked)
                      setIsDirty(true)
                    }}
                    value={formDownloadable}
                  />
                </SectionFormSettings.Root>
                <SectionFormSettings.Root>
                  <ToggleField
                    compact
                    horizontal
                    primary
                    label="Convert to Internal Form"
                    onChange={() => {
                      setInternalForm(!internalForm)
                      setIsDirty(true)
                    }}
                    value={internalForm}
                  />
                </SectionFormSettings.Root>

                {resourceType === 'form' && measuresEnabled ? (
                  <SectionFormSettings.Root>
                    <div className="relative flex w-full">
                      {!measuresEnabledForm ? (
                        <div className="w-full">
                          <ToggleField
                            compact
                            horizontal
                            primary
                            disabled={measuresEnabledForm}
                            label="Lock Form for Measures Tracking"
                            onChange={() => {
                              updateParams(['locking_form', 'true'])
                              setMeasuresEnabledForm(true)
                              setIsDirty(true)
                            }}
                            value={measuresEnabledForm}
                          />
                        </div>
                      ) : null}
                      {measuresEnabledForm ? <ChangeMeasuresTracking /> : null}
                    </div>
                  </SectionFormSettings.Root>
                ) : null}
              </div>
            </FormBuilderSettingsCard>
            {createPortal(
              isLockingForm ? (
                <ConfirmLockingFormModal
                  {...{
                    setMeasuresEnabledForm: (value) => {
                      setMeasuresEnabledForm(value)
                      setIsDirty(true)
                    },
                  }}
                />
              ) : null,
              document.body
            )}
          </>
        )
      }}
    </Consumer>
  )
}

export default FormBuilderSettingsGeneral
